import React from "react"
import { cn } from "src/utils/cn"
import { Tooltip, TooltipTrigger, TooltipContent } from "src/components/ui/tooltip"
import { DIMENSIONS, scoreBar, scoreColor } from "../constants"

// Compact four-segment PAIR meter (P · A · I · R) for a queue row. Each segment
// is a thin bar whose fill + color reflect that dimension's score; hover shows
// the score and what the dimension means.
export function PairBars({ dimensions = {}, size = "sm" }) {
  return (
    <div className="flex items-center gap-1.5">
      {DIMENSIONS.map(({ key, short, label, hint }) => {
        const score = dimensions?.[key]?.score
        return (
          <Tooltip key={key}>
            <TooltipTrigger asChild>
              <div className="flex flex-col items-center gap-1">
                <div className={cn("relative overflow-hidden rounded-full bg-slate-100", size === "sm" ? "h-1.5 w-9" : "h-2 w-12")}>
                  <div
                    className={cn("absolute inset-y-0 left-0 rounded-full transition-all", scoreBar(score))}
                    style={{ width: `${score ?? 0}%` }}
                  />
                </div>
                <span className={cn("text-[10px] font-semibold leading-none", scoreColor(score))}>
                  {short}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs font-medium">{label}: {score ?? "—"}/100</p>
              <p className="text-[11px] text-slate-300">{hint}</p>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}

// A larger, labeled PAIR breakdown used in the detail sheet — full-width bars
// with score numbers, evidence quotes, and extracted facts.
export function PairBreakdown({ dimensions = {} }) {
  return (
    <div className="space-y-4">
      {DIMENSIONS.map(({ key, label, hint }) => {
        const d = dimensions?.[key] || {}
        const score = d.score
        const evidence = d.evidence || []
        const extracted = d.extracted
        return (
          <div key={key} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-semibold text-slate-800">{label}</span>
                <span className="text-[11px] text-slate-400">{hint}</span>
              </div>
              <span className={cn("text-sm font-bold tabular-nums", scoreColor(score))}>
                {score ?? "—"}<span className="text-xs font-normal text-slate-400">/100</span>
              </span>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className={cn("absolute inset-y-0 left-0 rounded-full transition-all", scoreBar(score))} style={{ width: `${score ?? 0}%` }} />
            </div>
            {extracted && (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {extracted.budget_extracted && <Chip>Budget: {extracted.budget_extracted}</Chip>}
                {extracted.timeline_extracted && <Chip>Timeline: {extracted.timeline_extracted}</Chip>}
                {extracted.budget_fit === true && <Chip tone="good">Budget fits</Chip>}
                {extracted.budget_fit === false && <Chip tone="bad">Over budget</Chip>}
              </div>
            )}
            {evidence.length > 0 && (
              <ul className="space-y-1 pt-0.5">
                {evidence.map((q, i) => (
                  <li key={i} className="border-l-2 border-slate-200 pl-2 text-[12px] italic text-slate-500">
                    “{q}”
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Chip({ children, tone }) {
  const cls =
    tone === "good" ? "bg-emerald-50 text-emerald-700"
    : tone === "bad" ? "bg-red-50 text-red-700"
    : "bg-slate-100 text-slate-600"
  return <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-medium", cls)}>{children}</span>
}
